import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import e2iApi from '../../services/e2iApi';
import type { EnquiryWorkspace, Job, JobKind, JobLine } from '../../types/e2i';
import { JOB_STATUS } from '../../types/e2i';
import {
  draftLabel,
  errorText,
  formatAmount,
  formatDate,
  formatDateTime,
  formatQuantity,
  partyLabel,
  paymentTermsName,
  plural,
  quotationLabel,
  round2,
} from '../../utils/e2i';
import ActionDialog from './ActionDialog';
import ApprovalStages from './ApprovalStages';
import InfoField from './InfoField';
import JobDialog, { JobDialogInput } from './JobDialog';
import SapLine from './SapLine';
import { StatusChip } from './StatusChip';
import { WorkspaceTabProps, clientApprovedQuotation, liveJobs, quotationOfJob } from './workspace';

type JobAction = 'approve' | 'reject' | 'revert' | 'order' | 'complete';

const ACTION_TEXT: Record<JobAction, { title: string; button: string; help: string; comment: boolean; danger?: boolean }> = {
  approve: { title: 'Approve the job', button: 'Approve', help: 'It moves to the next stage; after the last stage the sales order can be prepared.', comment: false },
  reject: { title: 'Reject the job', button: 'Reject', help: 'The job ends here; its preparer is told why.', comment: true, danger: true },
  revert: { title: 'Send the job back', button: 'Send back', help: 'It goes back one stage, or to its preparer from the first stage.', comment: true },
  order: {
    title: 'Create the sales order',
    button: 'Create sales order draft',
    help: 'PRPO makes the SAP sales order as a DRAFT with the job\'s lines. Finance adds it in SAP through its approval; PRPO then sees the order (within a few minutes) and the job can be invoiced.',
    comment: false,
  },
  complete: {
    title: 'Works completed',
    button: 'Mark works completed',
    help: 'The enquiry gets register status 13 (Works completed). Invoice requests can still be raised for the lines not invoiced yet.',
    comment: false,
  },
};

/** The job (from the client-approved quotation, or exceptionally without one), its sales order and its completion. */
const JobTab: React.FC<WorkspaceTabProps> = ({ ws, config, companyId, currency, onWorkspace, notify, goTo }) => {
  const perms = ws.permissions;
  const jobs = useMemo(() => [...(ws.jobs || [])].sort((a, b) => b.jobId - a.jobId), [ws.jobs]);
  const [selectedId, setSelectedId] = useState<number | null>(jobs[0]?.jobId ?? null);
  const job = jobs.find((j) => j.jobId === selectedId) || jobs[0] || null;
  const [dialog, setDialog] = useState<{ open: boolean; kind: JobKind; job: Job | null }>({ open: false, kind: 'CAE', job: null });
  const [action, setAction] = useState<JobAction | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const newestId = jobs[0]?.jobId ?? null;
  useEffect(() => { if (newestId !== null) setSelectedId(newestId); }, [newestId]);

  const approvedQ = clientApprovedQuotation(ws);
  const canFromQuote = perms.canCreateJob && !!approvedQ && !liveJobs(ws).some((j) => j.quotationId === approvedQ.quotationId);
  const canWithout = perms.canCreateJob && ws.enquiry.status === 'Posted';
  const isBusy = busy !== null;

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(errorText(e, 'The action failed'));
    } finally {
      setBusy(null);
    }
  };

  const saveJob = async (input: JobDialogInput) => {
    let next: EnquiryWorkspace;
    if (dialog.job) next = await e2iApi.saveJob(dialog.job.jobId, input);
    else if (dialog.kind === 'CAL') next = await e2iApi.jobWithoutQuotation(ws.enquiry.enquiryId, { ...input, kind: 'CAL', calReason: input.calReason || '', lines: input.lines || [] });
    else if (approvedQ) next = await e2iApi.jobFromQuotation(approvedQ.quotationId, input);
    else throw new Error('No quotation approved by the client');
    onWorkspace(next);
    setDialog((d) => ({ ...d, open: false }));
    notify(dialog.job ? 'Job saved' : 'Job created', 'success');
  };

  const submit = (j: Job) => run('submit', async () => {
    const next = await e2iApi.submitJob(j.jobId);
    onWorkspace(next);
    const after = next.jobs.find((x) => x.jobId === j.jobId);
    notify(after?.status === 'Approved' ? 'No approval stage applies: the job is approved' : `${j.jobNo || 'The job'} was submitted for approval`, 'success');
  });

  const retryOrder = (j: Job) => run('order', async () => {
    const next = await e2iApi.createOrder(j.jobId);
    onWorkspace(next);
    const after = next.jobs.find((x) => x.jobId === j.jobId);
    if (after?.sap?.status === 'Failed') throw new Error(`SAP refused the sales order draft again${after.sap.error ? `: ${after.sap.error}` : ''}`);
    notify('The SAP sales order draft was created; finance adds it in SAP', 'success');
  });

  const confirm = async ({ comment }: { comment: string }) => {
    if (!action || !job) return;
    let next: EnquiryWorkspace;
    if (action === 'order') next = await e2iApi.createOrder(job.jobId);
    else if (action === 'complete') next = await e2iApi.completeJob(job.jobId);
    else next = await e2iApi.decideJob(job.jobId, action, comment || null);
    onWorkspace(next);
    setAction(null);
    const after = next.jobs.find((x) => x.jobId === job.jobId);
    if (action === 'order' && after?.sap?.status === 'Failed') {
      notify(`SAP refused the sales order draft${after.sap.error ? `: ${after.sap.error}` : ''}`, 'error');
      return;
    }
    notify(`${job.jobNo || 'Job'}: ${JOB_STATUS[(after?.status || job.status) as Job['status']]?.label || after?.status || ''}`, 'success');
  };

  const createButtons = (
    <>
      {canFromQuote ? (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, kind: 'CAE', job: null })} disabled={isBusy}>
          Create job from {quotationLabel(approvedQ?.quotationNo, approvedQ?.revision)}
        </Button>
      ) : null}
      {canWithout ? (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, kind: 'CAL', job: null })} disabled={isBusy}>
          Job without a quotation
        </Button>
      ) : null}
    </>
  );

  const jobDialog = (
    <JobDialog
      open={dialog.open}
      kind={dialog.job ? dialog.job.kind : dialog.kind}
      job={dialog.job}
      quotation={dialog.job ? quotationOfJob(ws, dialog.job) : approvedQ}
      enquiry={ws.enquiry}
      config={config}
      companyId={companyId}
      currency={currency}
      onClose={() => setDialog((d) => ({ ...d, open: false }))}
      onSave={saveJob}
    />
  );

  if (!job) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            {approvedQ
              ? 'No job yet. The job is created from the quotation the client approved; its priced lines become the sales order lines.'
              : 'No job yet. A job is created once the client approves a quotation, or exceptionally without one (a reason is required).'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>{createButtons}</Stack>
          {error ? <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert> : null}
        </Stack>
        {jobDialog}
      </Paper>
    );
  }

  const q = quotationOfJob(ws, job);
  const remaining = round2((Number(job.total) || 0) - (Number(job.invoicedTotal) || 0));
  const canEdit = perms.canCreateJob && (job.status === 'Draft' || (job.status === 'Approved' && job.kind === 'CAE' && !job.sap?.draftEntry));
  const cirCount = (ws.cirs || []).filter((c) => c.jobId === job.jobId && c.status !== 'Cancelled').length;
  const sapFailed = job.sap?.status === 'Failed';
  const showApproval = (job.approval?.stages || []).length > 0 || job.status === 'PendingApproval';

  const lineColumns: ResponsiveTableColumn<JobLine>[] = [
    { id: 'n', header: '#', cell: (l) => l.lineNum, width: 40 },
    { id: 'desc', header: 'Description', cell: (l) => l.description },
    { id: 'qty', header: 'Quantity', align: 'right', cell: (l) => `${formatQuantity(l.quantity)}${l.uom ? ` ${l.uom}` : ''}` },
    { id: 'price', header: `Unit price ${currency}`.trim(), align: 'right', hideOnMobile: true, cell: (l) => formatAmount(l.unitPrice) },
    { id: 'amount', header: `Amount ${currency}`.trim(), align: 'right', cell: (l) => <Typography variant="body2" fontWeight={600}>{formatAmount(l.amount)}</Typography> },
    {
      id: 'target',
      header: config.docMode === 'Service' ? 'Revenue account' : 'Sales item',
      hideOnMobile: true,
      cell: (l) => (config.docMode === 'Service' ? l.account : l.salesItem) || <Typography variant="caption" color="text.secondary">Default</Typography>,
    },
    { id: 'sapline', header: 'Order line', align: 'right', hideOnMobile: true, cell: (l) => (l.sapLine !== null && l.sapLine !== undefined ? l.sapLine : '—') },
    {
      id: 'invoice',
      header: 'Invoice request',
      cell: (l) => (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {l.cirNo ? <Typography variant="body2">{l.cirNo}</Typography> : <Typography variant="body2" color="text.secondary">Open</Typography>}
          {l.invoiced ? <Chip size="small" color="success" label="Invoiced" /> : null}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
          {jobs.length > 1 ? (
            <TextField id="e2i-job-select" select label="Job" value={job.jobId} onChange={(e) => setSelectedId(Number(e.target.value))} sx={{ minWidth: 220 }}>
              {jobs.map((j) => <MenuItem key={j.jobId} value={j.jobId}>{j.jobNo || 'Draft job'} · {JOB_STATUS[j.status]?.label || j.status}</MenuItem>)}
            </TextField>
          ) : null}
          <Box>
            <Typography variant="body2" fontWeight={600}>{job.jobNo || 'Draft job'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {job.kind === 'CAE' ? `From quotation ${q ? quotationLabel(q.quotationNo, q.revision) : ''}`.trim() : 'Without a quotation'}
            </Typography>
          </Box>
          <StatusChip status={JOB_STATUS[job.status]} fallback={job.status} />
          <Box sx={{ flexGrow: 1 }} />
          {canEdit ? <Button size="small" variant="outlined" onClick={() => setDialog({ open: true, kind: job.kind, job })} disabled={isBusy}>Edit job</Button> : null}
          {job.status === 'Draft' && perms.canCreateJob ? (
            <Button size="small" onClick={() => { void submit(job); }} disabled={isBusy}>{busy === 'submit' ? 'Submitting…' : 'Submit'}</Button>
          ) : null}
          {job.status === 'PendingApproval' && perms.canDecideJob ? (
            <>
              <Button size="small" variant="outlined" onClick={() => setAction('revert')} disabled={isBusy}>Send back</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setAction('reject')} disabled={isBusy}>Reject</Button>
              <Button size="small" onClick={() => setAction('approve')} disabled={isBusy}>Approve</Button>
            </>
          ) : null}
          {job.status === 'Approved' && perms.canCreateOrder && !sapFailed && job.sap?.status !== 'Posting' ? (
            <Button size="small" onClick={() => setAction('order')} disabled={isBusy}>Create sales order</Button>
          ) : null}
          {job.status === 'Ordered' && perms.canCompleteJob ? (
            <Button size="small" onClick={() => setAction('complete')} disabled={isBusy}>Works completed</Button>
          ) : null}
          {(job.status === 'Ordered' || job.status === 'Completed') ? (
            <Button size="small" variant="outlined" onClick={() => goTo('cirs')}>Invoice requests ({cirCount})</Button>
          ) : null}
          {createButtons}
        </Stack>
        {error ? <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>{error}</Alert> : null}
      </Paper>

      {job.sap?.status === 'Posting' ? <SapLine status="Posting">Creating the SAP sales order draft…</SapLine> : null}
      {sapFailed ? (
        <SapLine
          status="Failed"
          error={job.sap?.error}
          action={perms.canCreateOrder ? <Button color="inherit" size="small" onClick={() => { void retryOrder(job); }} disabled={isBusy}>{busy === 'order' ? 'Sending…' : 'Try again'}</Button> : undefined}
        >
          SAP refused the sales order draft
        </SapLine>
      ) : null}
      {job.status === 'OrderDrafted' ? (
        <SapLine status="Waiting">
          Sales order draft {draftLabel(job.sap?.draftNum, job.sap?.draftEntry || job.sap?.docEntry)} is in SAP: finance adds it through its approval. PRPO sees the order within a few minutes of that.
        </SapLine>
      ) : null}
      {(job.status === 'Ordered' || job.status === 'Completed') && job.sap ? (
        <SapLine status="Posted">SAP sales order {job.sap.docNum || ''}{job.sap.docEntry ? ` (entry ${job.sap.docEntry})` : ''}</SapLine>
      ) : null}
      {job.kind === 'CAL' && job.calReason ? <Alert severity="info">Without a quotation: {job.calReason}</Alert> : null}

      {showApproval ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Approval</Typography>
          <ApprovalStages approval={job.approval} emptyText="No approval stage applies to this job." />
        </Paper>
      ) : null}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>Job details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><InfoField label="Description" value={job.description} /></Grid>
          <Grid item xs={12} md={6}><InfoField label="Project" value={partyLabel(job.project)} /></Grid>
          <Grid item xs={6} md={3}><InfoField label="Job type" value={config.jobTypes.find((t) => t.code === job.jobType)?.name || job.jobType} /></Grid>
          <Grid item xs={6} md={3}><InfoField label="Payment terms" value={paymentTermsName(job.paymentTerms, config) || "The customer's"} /></Grid>
          <Grid item xs={6} md={3}><InfoField label="Location" value={job.location} /></Grid>
          <Grid item xs={6} md={3}><InfoField label="Cost center" value={job.costCenter} /></Grid>
          <Grid item xs={6} md={3}><InfoField label="Engineer" value={job.engineer?.name} /></Grid>
          <Grid item xs={6} md={3}>
            <InfoField label="Contract" value={job.contractStart || job.contractEnd ? `${formatDate(job.contractStart) || '…'} to ${formatDate(job.contractEnd) || '…'}` : ''} />
          </Grid>
          <Grid item xs={6} md={3}><InfoField label="Renews automatically" value={job.autoRenew ? 'Yes' : 'No'} /></Grid>
          <Grid item xs={6} md={3}><InfoField label="Grace period" value={job.gracePeriodDays !== null && job.gracePeriodDays !== undefined ? plural(job.gracePeriodDays, 'day') : ''} /></Grid>
          <Grid item xs={12}><InfoField label="Remarks" value={job.remarks} multiline /></Grid>
          {job.completedAt ? <Grid item xs={12}><InfoField label="Works completed" value={formatDateTime(job.completedAt)} /></Grid> : null}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Lines</Typography>
        <ResponsiveTable ariaLabel="Job lines" columns={lineColumns} rows={job.lines || []} getRowKey={(l) => l.lineId || l.lineNum} emptyMessage="No lines" />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Typography variant="body2">Total <strong>{formatAmount(job.total)}</strong> {currency}</Typography>
          <Typography variant="body2">Invoiced <strong>{formatAmount(job.invoicedTotal)}</strong> {currency}</Typography>
          <Typography variant="body2">Not invoiced <strong>{formatAmount(remaining)}</strong> {currency}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0 }}>Amounts before VAT. Each line is invoiced once, whole.</Typography>
      </Paper>

      {jobDialog}

      <ActionDialog
        open={!!action}
        id="e2i-job-action"
        title={action ? `${ACTION_TEXT[action].title}: ${job.jobNo || 'job'}` : ''}
        help={action ? ACTION_TEXT[action].help : undefined}
        confirmLabel={action ? ACTION_TEXT[action].button : 'OK'}
        danger={action ? ACTION_TEXT[action].danger : false}
        commentLabel={action === 'approve' || action === 'reject' || action === 'revert' ? 'Comment' : undefined}
        commentRequired={action ? ACTION_TEXT[action].comment : false}
        onClose={() => setAction(null)}
        onConfirm={confirm}
      />
    </Stack>
  );
};

export default JobTab;
